import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { filterJobRoles } from "../../../../jobRoles";
import host from "../../../../../../global";
import { experienceLevelToMinMax } from "../utils/jdUtils";

/**
 * Manages all Step 1 (Job Details) state for the Custom JD flow.
 * Owns: formData, drag/drop/upload state, job role autocomplete, refs, and all handlers.
 */
export function useJobDetailsState({ accessToken }) {
  const jobRoleInputRef = useRef(null);
  const jobRoleContainerRef = useRef(null);
  const suggestionsRef = useRef(null);
  const suggestionRefs = useRef([]);
  const fileInputRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  const [formData, setFormData] = useState({
    jobRole: "",
    minExp: "",
    maxExp: "",
    location: "",
    responsibilities: "",
    errors: { jobRole: "", responsibilities: "" },
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isUploadingJD, setIsUploadingJD] = useState(false);
  const [isGeneratingJD, setIsGeneratingJD] = useState(false);
  const [showJobRoleSuggestions, setShowJobRoleSuggestions] = useState(false);
  const [jobRoleSuggestions, setJobRoleSuggestions] = useState([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);

  // Click outside — close suggestion dropdown
  useEffect(() => {
    const handler = (e) => {
      if (
        jobRoleContainerRef.current &&
        !jobRoleContainerRef.current.contains(e.target)
      ) {
        setShowJobRoleSuggestions(false);
        setSelectedSuggestionIndex(-1);
      }
    };
    if (showJobRoleSuggestions) {
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }
  }, [showJobRoleSuggestions]);

  // Auto-scroll selected suggestion into view
  useEffect(() => {
    const el = suggestionRefs.current[selectedSuggestionIndex];
    if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selectedSuggestionIndex]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    };
  }, []);

  const updateFormData = useCallback((updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleJobRoleChangeWithSuggestions = useCallback(
    (e) => {
      let value = (e.target.value || "").replace(/[^a-zA-Z0-9 ]/g, "");
      if (/^\d+$/.test(value)) {
        updateFormData({ jobRole: "" });
        setJobRoleSuggestions([]);
        setShowJobRoleSuggestions(false);
        return;
      }
      updateFormData({ jobRole: value });

      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = setTimeout(() => {
        if (value.length >= 2) {
          const suggestions = Array.from(new Set(filterJobRoles(value)));
          setJobRoleSuggestions(suggestions);
          setShowJobRoleSuggestions(suggestions.length > 0);
          setSelectedSuggestionIndex(-1);
        } else {
          setJobRoleSuggestions([]);
          setShowJobRoleSuggestions(false);
        }
      }, 150);
    },
    [updateFormData]
  );

  const handleSuggestionSelect = useCallback(
    (role) => {
      updateFormData({ jobRole: role });
      setJobRoleSuggestions([]);
      setShowJobRoleSuggestions(false);
      setSelectedSuggestionIndex(-1);
      setFormData((prev)=>({
        ...prev,
        minExp: "",
        maxExp: "",
        responsibilities: "",
      }))
    },
    [updateFormData]
  );

  const handleJobRoleKeyDown = useCallback(
    (e) => {
      if (!showJobRoleSuggestions || jobRoleSuggestions.length === 0) {
        if (e.key === "Tab" || e.key === "Enter") {
          setShowJobRoleSuggestions(false);
          setSelectedSuggestionIndex(-1);
        }
        return;
      }
      const maxIndex = jobRoleSuggestions.length - 1;
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedSuggestionIndex((i) => (i < maxIndex ? i + 1 : 0));
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedSuggestionIndex((i) => (i > 0 ? i - 1 : maxIndex));
          break;
        case "Enter":
          e.preventDefault();
          if (selectedSuggestionIndex >= 0) {
            handleSuggestionSelect(jobRoleSuggestions[selectedSuggestionIndex]);
          } else {
            setShowJobRoleSuggestions(false);
            setSelectedSuggestionIndex(-1);
          }
          break;
        case "Tab":
        case "Escape":
          setShowJobRoleSuggestions(false);
          setSelectedSuggestionIndex(-1);
          break;
        default:
          break;
      }
    },
    [
      showJobRoleSuggestions,
      jobRoleSuggestions,
      selectedSuggestionIndex,
      handleSuggestionSelect,
    ]
  );

  const handleExperienceSelect = useCallback(
    (value) => {
      if (!value) return;
      const { minExp, maxExp } = experienceLevelToMinMax(value);
      setFormData((prev) => ({
        ...prev,
        minExp,
        maxExp,
        errors: { ...prev.errors, minExp: "", maxExp: "", experience: "" },
      }));
    },
    []
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const processFile = useCallback(
    async (file) => {
      if (!file) return;
      const allowed = [
        "application/pdf",
        "text/plain",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!allowed.includes(file.type)) {
        toast.error("Only PDF, DOCX, DOC, or TXT files are allowed.");
        return;
      }
      setIsUploadingJD(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await fetch(`${host}/extract-jd-text`, {
          method: "POST",
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: "include",
          body: fd,
        });
        if (!res.ok) throw new Error("Failed to parse file");
        const data = await res.json();
        if (data.extracted_text) {
          updateFormData({ responsibilities: data.extracted_text });
        }
      } catch (_) {
        toast.error("Failed to parse file. Please try again.");
      } finally {
        setIsUploadingJD(false);
        setIsDragging(false);
      }
    },
    [accessToken, updateFormData]
  );

  const handleDropJD = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files?.[0];
      if (file) processFile(file);
    },
    [processFile]
  );

  const handleFileInputChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (file) processFile(file);
    },
    [processFile]
  );

  const handleGenerateJDWithAI = useCallback(async () => {
    if (!formData.jobRole.trim()) return;
    setIsGeneratingJD(true);
    try {
      const res = await fetch(`${host}/generate-jd-with-ai`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          jobRole: formData.jobRole,
          minExp: formData.minExp,
          maxExp: formData.maxExp,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Generation failed");

      if (!data.is_valid) {
        updateFormData({
          errors: {
            ...formData.errors,
            jobRole: "Not a valid job role. Please enter a valid position title.",
          },
        });
        return;
      }

      const generated = data.job_description || data.responsibilities || "";
      if (generated) {
        updateFormData({
          responsibilities: generated,
          errors: { ...formData.errors, jobRole: "", responsibilities: "" },
        });
      }
    } catch (_) {
      toast.error("Failed to generate JD. Please try again.");
    } finally {
      setIsGeneratingJD(false);
    }
  }, [formData.jobRole, formData.minExp, formData.maxExp, formData.errors, accessToken, updateFormData]);

  const isStepOneNextDisabled = useMemo(() => {
    const wordCount = formData.responsibilities.trim().split(/\s+/).filter(Boolean).length;
    return (
      !formData.jobRole.trim() ||
      formData.minExp === "" ||
      formData.maxExp === "" ||
      !formData.responsibilities.trim() ||
      wordCount < 50 ||
      wordCount > 1500
    );
  }, [formData]);

  return {
    // State
    formData,
    setFormData, // needed by orchestrator edit-mode load effect
    isDragging,
    isUploadingJD,
    isGeneratingJD,
    showJobRoleSuggestions,
    jobRoleSuggestions,
    selectedSuggestionIndex,
    // Refs
    jobRoleInputRef,
    jobRoleContainerRef,
    suggestionsRef,
    suggestionRefs,
    fileInputRef,
    // Updater
    updateFormData,
    // Handlers
    handleJobRoleChangeWithSuggestions,
    handleSuggestionSelect,
    handleJobRoleKeyDown,
    handleExperienceSelect,
    handleDragOver,
    handleDragLeave,
    handleDropJD,
    handleFileInputChange,
    handleGenerateJDWithAI,
    // Derived
    isStepOneNextDisabled,
  };
}
